import { apiClient } from '../apiClient';

/**
 * FitFinder session + catalog (Krysalis_Backend: `app/api/v1/fitfinder_sessions.py`,
 * `fitfinder_catalog.py`, `product_solutions.py`). Base URL includes `/api/v1`.
 *
 * **Listing APIs:** `getFitfinderCatalog` returns **ACTIVE** solutions with **`fitfinder_enabled`**
 * (public-style catalog). `listProductSolutions` includes **non-ACTIVE** rows for partner
 * publish/transition workflows — use the right one for the UI (see Fitfinder catalog page).
 */

/**
 * Privacy-safe fields from pt-product-deal (no raw summaries). Tier/snapshot/job fields may be
 * absent until the scheduled match job has written them; `deal_artifact_item_id` is present when
 * a deal row exists.
 */
export interface FitfinderDealMatchSnapshot {
  deal_artifact_item_id?: string;
  fitfinder_match_score_tier?: string;
  fitfinder_matching_inputs_ready?: boolean;
  fitfinder_matching_inputs_snapshot?: {
    solution_summary_len?: number;
    situation_approx_len?: number;
  };
  fitfinder_match_job_last_run_at?: string;
  fitfinder_match_job_version?: number;
}

export interface FitfinderSession {
  id: string;
  solution_artifact_item_id: string;
  situation_artifact_item_id: string | null;
  owning_company_id: string;
  prospect_user_id: string | null;
  state: string;
  metadata: Record<string, unknown>;
  stripe_checkout_session_id?: string | null;
  stripe_payment_intent_id?: string | null;
  in_progress_paid_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** Present on transition response when Stripe Checkout is required (ISSUE-252). */
  payment_required?: boolean;
  checkout_url?: string;
  /** Present when GET session can resolve a FITFINDER deal row for solution/situation pair. */
  deal_match?: FitfinderDealMatchSnapshot | null;
}

export interface CreateFitfinderSessionBody {
  solution_artifact_item_id: string;
  situation_artifact_item_id?: string;
  metadata?: Record<string, unknown>;
}

export async function createFitfinderSession(
  body: CreateFitfinderSessionBody
): Promise<FitfinderSession> {
  const res = await apiClient.post<FitfinderSession>('/fitfinder/sessions', body);
  return res.data;
}

export async function getFitfinderSession(sessionId: string): Promise<FitfinderSession> {
  const res = await apiClient.get<FitfinderSession>(`/fitfinder/sessions/${sessionId}`);
  return res.data;
}

export async function transitionFitfinderSession(
  sessionId: string,
  toState: string,
  options?: { return_origin?: string }
): Promise<FitfinderSession> {
  const res = await apiClient.post<FitfinderSession>(`/fitfinder/sessions/${sessionId}/transition`, {
    to_state: toState,
    ...(options?.return_origin ? { return_origin: options.return_origin } : {}),
  });
  return res.data;
}

/**
 * Catalog entry shape from **GET /fitfinder/catalog** — **ACTIVE** + **fitfinder_enabled** only
 * (`solution_row_to_catalog_entry` on the backend).
 */
export interface FitfinderCatalogEntry {
  solution_id: string;
  ideal_situation_ref?: unknown;
  ideal_actor_ref?: unknown;
  prequalification_threshold?: unknown;
  match_disposition?: string;
  situation_type?: string;
  actor_type?: string;
  fitfinder_enabled?: boolean;
  [key: string]: unknown;
}

export interface FitfinderCatalogResponse {
  solutions: FitfinderCatalogEntry[];
  company_id: string;
}

/** ACTIVE FitFinder-enabled solutions only — not the full product-solution list. */
export async function getFitfinderCatalog(params: {
  company_id?: string;
  match_disposition?: string;
  situation_type?: string;
  actor_type?: string;
}): Promise<FitfinderCatalogResponse> {
  const res = await apiClient.get<FitfinderCatalogResponse>('/fitfinder/catalog', { params });
  return res.data;
}

export interface ListFitfinderSessionsResponse {
  sessions: FitfinderSession[];
  limit: number;
  offset: number;
}

/** GET /fitfinder/sessions — requires company_id and/or solution_artifact_item_id for non-superusers. */
export async function listFitfinderSessions(params: {
  company_id?: string;
  solution_artifact_item_id?: string;
  state?: string;
  limit?: number;
  offset?: number;
}): Promise<ListFitfinderSessionsResponse> {
  const res = await apiClient.get<ListFitfinderSessionsResponse>('/fitfinder/sessions', { params });
  return res.data;
}

/**
 * **GET /product-solutions** — all workflow states when `include_inactive` is true (partner:
 * publish INITIALIZED → ACTIVE, etc.). Prefer over `getFitfinderCatalog` when the UI needs drafts.
 */
export interface ProductSolutionEntry extends FitfinderCatalogEntry {
  artifact_item_id: string;
}

export async function listProductSolutions(params: {
  company_id: string;
  include_inactive?: boolean;
}): Promise<ProductSolutionEntry[]> {
  const res = await apiClient.get<ProductSolutionEntry[]>('/product-solutions', { params });
  return res.data;
}

/** POST /product-solutions/{id}/transition — INITIALIZED → ACTIVE → CLOSED. */
export async function transitionProductSolution(
  artifactItemId: string,
  toState: 'ACTIVE' | 'CLOSED'
): Promise<ProductSolutionEntry> {
  const res = await apiClient.post<ProductSolutionEntry>(`/product-solutions/${artifactItemId}/transition`, {
    to_state: toState,
  });
  return res.data;
}

export interface ProductSolutionAvailableTransitionsResponse {
  transitions: Array<Record<string, unknown>>;
}

export async function getProductSolutionAvailableTransitions(
  artifactItemId: string
): Promise<ProductSolutionAvailableTransitionsResponse> {
  const res = await apiClient.get<ProductSolutionAvailableTransitionsResponse>(
    `/product-solutions/${artifactItemId}/available-transitions`
  );
  return res.data;
}
